export const CLIENT_MANAGEMENT_GUIDE = `# Client Management & Organization Linking Guide

## Overview

VoiceThera's client management system enables practitioners to create personalized meditation sessions for individual clients. Clients can be personal (unaffiliated) or linked to organization employees, which determines credit usage and enables organization-contextualized sessions.

**Key Capabilities:**
- Manage up to 100 active clients per practitioner
- Link clients to organization employees for B2B wellness programs
- Auto-populate employee data from organization profiles
- Track session delivery and client feedback
- Set client-specific therapeutic directives

---

## Creating Clients

### Accessing Client Management

1. Navigate to **Dashboard → Clients**
2. Click **"Add New Client"** button
3. Choose client type:
   - **Personal Client**: Individual not affiliated with any organization
   - **Organization Employee**: Link to existing organization employee

### Required Fields

Every client must have:

- **First Name** *(required)*
- **Last Name** *(optional but recommended)*
- **Biography** *(required, 100-3000 characters)*

The biography is **critical** for session personalization. See biography best practices below.

### Optional Fields

Enhance personalization with:

- **Email**: For session delivery links
- **Phone**: Contact information
- **Preferred Name**: How client prefers to be addressed (e.g., "Alex" instead of "Alexander")
- **Name Pronunciations**: JSON field for pronunciation guidance (e.g., \`{"first_name": "mah-REE-ah"}\`)
- **Session Directives**: Specific therapeutic instructions (e.g., "Avoid religious language", "Focus on body scan techniques")
- **Content Requirements**: Array of required themes (e.g., ["gratitude", "self-compassion"])
- **Content Exclusions**: Array of topics to avoid (e.g., ["religious references", "military metaphors"])
- **Notes**: Private practitioner notes (never included in sessions)

---

## Biography Best Practices

The client biography is the **foundation** for session personalization. Follow these guidelines:

### What to Include

✅ **Current life circumstances**: "Working full-time while caring for elderly parent"
✅ **Specific stressors**: "Deadline pressure at work, sleep deprivation from toddler"
✅ **Emotional patterns**: "Tendency to internalize criticism, people-pleasing behavior"
✅ **Physical manifestations**: "Tension headaches, jaw clenching, shallow breathing"
✅ **Goals or aspirations**: "Wants to set boundaries without guilt, improve sleep quality"
✅ **Therapeutic history**: "Previously tried CBT, responds well to somatic techniques"

### What to Avoid

❌ **Generic statements**: "Stressed person" → Too vague
❌ **Overly formal language**: Write in natural, descriptive voice
❌ **Diagnostic labels without context**: "Anxiety disorder" → Better: "Experiences racing thoughts and chest tightness before presentations"
❌ **Confidential information**: Avoid details that shouldn't be read aloud in sessions

### Length Guidelines

- **Minimum**: 100 characters (system enforced)
- **Recommended**: 300-800 characters
- **Maximum**: 3000 characters

Short biographies (under 200 chars) result in generic sessions. Aim for rich, contextual detail.

### Example Biography

**Poor**:
> "Client is stressed and has trouble sleeping."

**Good**:
> "Sarah is a 34-year-old emergency room nurse working night shifts. She struggles with intrusive thoughts about patient cases when trying to sleep during the day, leading to chronic exhaustion. She describes feeling 'wired but tired' - physically drained but mentally hypervigilant. Sarah's goal is to develop a healthy boundary between work trauma and personal life, and to reclaim restful sleep without medication."

---

## Organization Linking

### Understanding Organization Access

Practitioners cannot see all organizations - only those that have **explicitly granted access**. Organizations control which practitioners can create sessions for their employees.

### Checking Granted Organizations

1. Navigate to **Dashboard → Organizations**
2. View list of organizations that have granted you access
3. See access status, granted date, and expiration (if any)

If you don't see an organization, request access:
1. Contact the organization admin directly
2. Provide your practitioner email
3. Org admin grants access via their admin dashboard

### Linking a Client to an Organization

When creating a new client:

1. In the client form, select **"Link to Organization Employee"**
2. Choose organization from **"Select Organization"** dropdown
   - Only shows organizations that granted you access
   - If empty, you have no active organization access grants
3. Choose employee from **"Select Employee"** dropdown
   - Shows all active employees in the selected organization
   - Displays employee names according to their preference settings
4. Auto-population occurs:
   - Employee biography → Client biography (pre-filled, editable)
   - Employee role → Client employee role (pre-filled, editable)
   - Department → Client department (pre-filled, editable)
   - Organization context → Available for session generation

### Auto-Population Details

When you select an organization employee:

**Pre-filled (editable by practitioner):**
- Client biography (from employee biography)
- Employee role (from employee role)
- Department (from employee department)

**Retrieved for session context (not editable):**
- Organization biography
- Core values
- Mission statement
- Culture description
- Wellness philosophy

**Practitioner can still customize:**
- Session directives
- Content requirements/exclusions
- Notes

---

## Credit Implications

### Visual Credit Indicator

When creating sessions for clients, the form displays:

**Organization-linked client:**
> 🏢 **Organization credits will be used**
> Sessions will be paid for by [Organization Name]

**Personal client:**
> 👤 **Personal credits will be used**
> Sessions will deduct from your practitioner credit balance

This indicator appears in real-time as you select clients.

### Checking Organization Credit Balance

Before creating sessions for org-linked clients:

1. View client details
2. Check organization credit status:
   - ✅ **Credits available**: Organization has sufficient balance
   - ⚠️ **Low credits**: Organization running low (contact admin)
   - ❌ **No credits**: Session will fail, org must purchase more

### What Happens When Org Runs Out of Credits

If you attempt to generate a session for an org-linked client when the organization has insufficient credits:

1. **Session generation fails** with error message
2. **Your personal credits are NOT charged** (fallback does not occur)
3. **Action required**: Contact organization admin to purchase more credits

**Workaround**: If urgent, you can create a duplicate personal client (not org-linked) and use your own credits.

---

## Client Limits

### Active Client Limit

Each practitioner can manage:
- **Maximum 100 active clients** at any time
- Inactive clients do not count toward limit
- No limit on total historical clients (active + inactive)

### Reaching the Limit

When you have 100 active clients:
- "Add New Client" button becomes disabled
- System shows: "Maximum active clients reached (100/100)"
- **Solution**: Deactivate clients you no longer actively serve

---

## Deactivation vs Deletion

### Deactivating Clients (Recommended)

**Soft delete** - Client record preserved but hidden:

1. Navigate to client details
2. Click **"Deactivate Client"**
3. Confirm action

**Effects:**
- ✅ Client removed from active client list
- ✅ Historical sessions remain accessible
- ✅ Does not count toward 100-client limit
- ✅ Can be reactivated anytime

**When to deactivate:**
- Client no longer actively receiving sessions
- Temporary pause in services
- Want to preserve historical records

### Deleting Clients (Permanent)

**Hard delete** - Client record permanently removed:

1. Navigate to client details
2. Click **"Delete Client"** (requires confirmation)
3. Confirm permanent deletion

**Effects:**
- ❌ Client record permanently deleted
- ❌ Historical sessions orphaned (remain but not linked to client)
- ❌ Cannot be undone

**When to delete:**
- Client requested data deletion
- Duplicate client record created by mistake
- Compliance requires removal

**⚠️ Warning**: Deletion is irreversible. Use deactivation instead in most cases.

---

## Group Sessions

Link multiple clients to family or team groups:

### Creating Groups

1. Navigate to **Groups** tab
2. Click **"Create Group"**
3. Add group details:
   - Group name
   - Group type (Family, Team, Custom)
   - Description
4. Add participants (clients)

### Group Limits by Subscription

| Subscription Tier | Max Groups |
|------------------|------------|
| Free (Individual or Practitioner) | 1 group |
| Basic Individual | 4 groups |
| Pro Individual | 10 groups |
| Basic Practitioner | 4 groups |
| Pro Practitioner | Unlimited |

### Linking Clients to Groups

When creating/editing a client:
1. Select **"Group Session"** dropdown
2. Choose existing group
3. Client becomes part of that group

### Group Session Generation

When generating a session for a group:
- All participant biographies blended into session context
- Session acknowledges multiple participants
- Suitable for family meditations, team wellness sessions
- Group sessions count against practitioner's credit pool

---

## Session Directives

Set client-specific therapeutic instructions that override default behavior:

### Common Directives

**Language preferences:**
- "Avoid religious or spiritual language"
- "Use gender-neutral pronouns throughout"
- "Prefer medical terminology over metaphors"

**Technique preferences:**
- "Focus on body scan techniques, minimize visualization"
- "Include breathwork at beginning and end"
- "Avoid silence longer than 10 seconds"

**Sensitivity considerations:**
- "No references to pregnancy or parenting"
- "Avoid military or combat metaphors"
- "Use gentle language for trauma-informed care"

### How Directives Work

Session directives are:
- Passed to AI prompt generation system
- Applied to **every session** created for that client
- Overrides category defaults
- Not visible to client (practitioner-only settings)

### Editing Directives

1. Navigate to client details
2. Scroll to **"Session Directives"** field
3. Enter or update directives
4. Click **"Save Client"**
5. All future sessions will honor new directives

---

## Content Requirements & Exclusions

### Content Requirements (Array)

Themes or elements that **must** appear in sessions:

**Examples:**
- \`["gratitude", "self-compassion"]\` → Every session must include gratitude and self-compassion themes
- \`["breathwork", "body awareness"]\` → Sessions must incorporate breathing exercises and somatic focus
- \`["nature imagery", "water sounds"]\` → Sessions must use nature scenes and water metaphors

### Content Exclusions (Array)

Topics or themes to **avoid** in sessions:

**Examples:**
- \`["religious references", "spiritual language"]\` → No mentions of prayer, God, soul, etc.
- \`["military metaphors", "combat language"]\` → No "battle", "fight", "warrior", etc.
- \`["parenting", "children"]\` → No references to kids or family life
- \`["weight loss", "body shape"]\` → Avoid body image topics

### Setting Requirements/Exclusions

1. In client form, scroll to **"Content Requirements"** field
2. Enter comma-separated values: \`gratitude, self-compassion, nature\`
3. Scroll to **"Content Exclusions"** field
4. Enter comma-separated values: \`religious, military metaphors\`
5. Save client

These filters apply to **all sessions** for that client.

---

## Session History & Feedback

### Viewing Client Session History

1. Navigate to client details
2. Click **"Session History"** tab
3. See chronological list of all sessions:
   - Date created
   - Session title
   - Duration
   - Category
   - Delivery status
   - Client feedback (if provided)

### Client Feedback Tracking

When clients provide feedback via shared session links:

**Feedback data captured:**
- Rating (1-5 stars)
- Written comments
- Helpful elements
- Improvement suggestions

**Practitioner view:**
1. Session history shows feedback icon
2. Click to view detailed feedback
3. Use insights to refine future sessions

**Privacy note**: Feedback is visible to practitioner only, not shared with organizations.

---

## Name Display Preferences

### Understanding Name Preferences

Organization employees set their own name display preference:

- **Preferred Name**: "Call me Alex" (uses preferred_name field)
- **First Name Only**: "Call me Alexander" (uses first_name only)
- **Full Name**: "Call me Alexander Johnson" (uses first_name + last_name)

### How This Affects Practitioners

When viewing organization employees:
- Employee list shows names according to their preference
- Dropdown shows preferred display format
- Sessions address client by preferred name

**Example:**
- Profile: Alexander Johnson
- Preferred name: "Alex"
- Preference: "preferred"
- **Displays as**: Alex (not Alexander)

You cannot override employee name preferences - they control how they appear throughout the system.

---

## Real-Time Updates

### Employee List Auto-Refresh

Organization employee lists update automatically:
- New employees appear immediately after accepting invitations
- Deactivated employees disappear from dropdowns
- Department changes reflect in real-time

**If you don't see a new employee:**
1. Refresh the page
2. Check if employee accepted their invitation
3. Verify organization admin activated the employee

### Subscription Changes

If client's organization subscription changes:
- Credit balance updates in real-time
- Access may be revoked if subscription cancelled
- Sessions in progress are not interrupted

---

## Frequently Asked Questions

### Client Limits

**Q: Can I exceed 100 active clients?**
A: No, this is a hard limit. Deactivate clients you no longer actively serve to free up slots.

**Q: Do inactive clients count toward the limit?**
A: No, only active clients count. Deactivated clients do not count.

**Q: Can I reactivate deactivated clients?**
A: Yes, navigate to client details and click "Reactivate". They will count toward your active limit again.

### Organization Linking

**Q: Why can't I see an organization in the dropdown?**
A: The organization must explicitly grant you access. Contact their admin to request access.

**Q: Can I link a client to multiple organizations?**
A: No, each client can only be linked to one organization employee at a time.

**Q: What if the employee leaves the organization?**
A: The client record remains but organization access may be revoked. Convert to personal client if needed.

**Q: Can I edit the auto-populated biography?**
A: Yes, all auto-populated fields are editable. Organization data is used as a starting point but you can customize.

### Credits

**Q: If I create a session for an org-linked client, who pays?**
A: The organization pays from their credit pool. Your personal credits are not affected.

**Q: What if the organization runs out of credits mid-session?**
A: Session generation will fail. Contact the organization admin to purchase more credits before retrying.

**Q: Can I switch a client from organization-linked to personal?**
A: Not directly. You would need to create a new personal client record (unlinked) and deactivate the org-linked one.

### Feedback & Delivery

**Q: How do clients access their sessions?**
A: You generate a shareable link with expiration date. Send the link via email, SMS, or in-person.

**Q: Can I see if a client listened to their session?**
A: Shared session links track access count and last accessed timestamp. Check session details for this data.

**Q: How long are shared links valid?**
A: You set the expiration when creating the link (e.g., 7 days, 30 days, 1 year).

---

## Support

For client management issues:
- Email: support@voicethera.com
- Chat: Use in-app support
- Related guides: Credit System Guide, Organization Sessions Guide

**Last Updated**: January 2025
**Version**: 1.0
`;
